/**
 * BaseSpeed (Foundry: rule-element/base-speed.ts): a base speed for one movement type. `land`
 * replaces the ancestry's base; other selectors add a speed type. The value may be a formula
 * over another speed (`@actor.system.movement.speeds.land.value`), which the movement
 * preparation orders and classifies (../movement.ts).
 */
import { cleanRuleValue, resolveInjectedProperties, resolveValue } from "../resolve";
import type { EngineState } from "../state";
import type { BaseSpeedSynthetic } from "../synthetics";
import { isMovementType, type MovementType } from "../values";
import type { RuleHandler, RuleInstance } from "./base";

export interface BaseSpeedRuleData {
  selector: string;
  value: string | number;
  force: boolean;
}

const SPEED_TYPE_PATTERN = /movement\.speeds\.(land|burrow|climb|fly|swim)/g;

/** The other movement types a formula reads (Foundry: getSpeedFormulaDependsOn). */
export function speedFormulaDependsOn(formula: unknown, selfType: MovementType): MovementType[] {
  if (typeof formula !== "string") return [];
  const found = new Set<MovementType>();
  for (const match of formula.matchAll(SPEED_TYPE_PATTERN)) {
    const type = match[1];
    if (isMovementType(type) && type !== selfType) found.add(type);
  }
  return [...found];
}

function parse(rule: RuleInstance): void {
  const s = rule.source;
  if (typeof s.selector !== "string" || !s.selector.trim()) return rule.fail("selector: must be a non-empty string");
  const value = cleanRuleValue(s.value);
  if (typeof value !== "string" && typeof value !== "number") return rule.fail("A value must be a number or string");
  if (s.force !== undefined && typeof s.force !== "boolean") return rule.fail("force: must be a boolean");
  const data: BaseSpeedRuleData = { selector: s.selector.trim().replace(/-speed$/, ""), value, force: s.force === true };
  rule.data = data as unknown as Record<string, unknown>;
}

function beforePrepareData(rule: RuleInstance, state: EngineState): void {
  if (rule.ignored) return;
  const data = rule.data as unknown as BaseSpeedRuleData;
  const speedType = resolveInjectedProperties(rule, state, data.selector);
  if (rule.ignored) return;
  if (!isMovementType(speedType)) return rule.fail("Unrecognized or missing selector");
  const dependsOn = speedFormulaDependsOn(data.value, speedType);
  const deferred = (params: { test?: Set<string> } = {}): BaseSpeedSynthetic | null => {
    if (!rule.test(state, params.test)) return null;
    const value = Math.trunc(Number(resolveValue(rule, state, data.value)));
    if (rule.ignored) return null;
    if (!(value > 0 || (data.force && value === 0))) {
      if (!Number.isInteger(value)) rule.fail("Failed to resolve value");
      return null;
    }
    return { type: speedType, value, source: rule.reducedLabel, force: data.force, formula: data.value, dependsOn };
  };
  (state.synthetics.movementTypes[speedType] ??= []).push({ dependsOn, deferred, test: (params = {}) => rule.test(state, params.test) });
}

export const BaseSpeedHandler: RuleHandler = { key: "BaseSpeed", parse, beforePrepareData };
